import { useMemo } from 'react'
import { Check, Info, Loader2, X } from 'lucide-react'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { useDepartments, useClasses, useSubclasses } from '@/hooks/useHierarchy'
import { usePlanNameValidation } from '@/hooks/usePlanNameValidation'
import { generatePlanNameSuggestion } from '@/utils/planNameGenerator'
import { cn } from '@/utils/cn'
import { StepHeader } from './StepHeader'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export function NameYourPlanStep() {
  const {
    selectedMethod,
    selectedDepartmentId,
    selectedClassId,
    selectedHierarchyId,
    selectedSourcePlan,
    historicalDataSourceType,
    historicalSourcePlan,
    planName,
    setPlanName,
    setDepartment,
    setClass,
    setSubclass,
  } = usePlanWizardStore()

  const isCopyFlow = selectedMethod === 'copy'
  const isHistoricalDataFlow = selectedMethod === 'historicalData'

  const { data: departments, isFetching: isDepartmentsLoading } =
    useDepartments()
  const { data: classes, isFetching: isClassesLoading } =
    useClasses(selectedDepartmentId)
  const { data: subclasses, isFetching: isSubclassesLoading } =
    useSubclasses(selectedClassId)

  // Use the validation hook with debouncing
  const { isValidating, isValid, errorMessage } = usePlanNameValidation(
    planName,
    { debounceMs: 300, enabled: planName.trim().length > 0 }
  )

  // Get selected items for suggestion generation
  const selectedDepartment = departments?.find(
    d => d.id === selectedDepartmentId
  )
  const selectedClass = classes?.find(c => c.id === selectedClassId)
  const selectedSubclass = subclasses?.find(s => s.id === selectedHierarchyId)

  // Generate suggestion using utility function - all plans are now CORE
  const suggestion = useMemo(() => {
    // For copy flow
    if (isCopyFlow && selectedSourcePlan) {
      return generatePlanNameSuggestion({
        sourcePlan: selectedSourcePlan,
        department: selectedDepartment,
        classItem: selectedClass,
        subclass: selectedSubclass,
      })
    }

    // For historical data flow with plan source (same as copy flow)
    if (
      isHistoricalDataFlow &&
      historicalDataSourceType === 'plan' &&
      historicalSourcePlan
    ) {
      return generatePlanNameSuggestion({
        sourcePlan: historicalSourcePlan,
        department: selectedDepartment,
        classItem: selectedClass,
        subclass: selectedSubclass,
      })
    }

    // For historical data flow with file source (same as blank flow)
    if (isHistoricalDataFlow && historicalDataSourceType === 'file') {
      return generatePlanNameSuggestion({
        department: selectedDepartment,
        classItem: selectedClass,
        subclass: selectedSubclass,
      })
    }

    // For blank flow
    return generatePlanNameSuggestion({
      department: selectedDepartment,
      classItem: selectedClass,
      subclass: selectedSubclass,
    })
  }, [
    isCopyFlow,
    isHistoricalDataFlow,
    selectedSourcePlan,
    historicalDataSourceType,
    historicalSourcePlan,
    selectedDepartment,
    selectedClass,
    selectedSubclass,
  ])

  const handleSuggestionClick = () => {
    if (suggestion) {
      setPlanName(suggestion)
    }
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-[90%] mx-auto">
      {/* Header */}
      <StepHeader
        title="Name Your Plan"
        description="Enter a unique name following the naming convention"
      />

      {/* Product Hierarchy Section - ALL FLOWS */}
      <div className="bg-white p-6 rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]">
        <h3 className="text-[16px] font-semibold text-[#1C1C1C] mb-4">
          Product Hierarchy
        </h3>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Department */}
          <div className="flex-1">
            <label className="block text-[14px] font-medium text-[#556374] mb-2">
              Department{' '}
              <span className="text-[#8C95A1] font-normal">(Optional)</span>
            </label>
            <Select
              value={selectedDepartmentId ?? ''}
              onValueChange={setDepartment}
              disabled={isDepartmentsLoading}
            >
              <SelectTrigger className="w-full">
                {isDepartmentsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <SelectValue placeholder="Select an option" />
                )}
              </SelectTrigger>
              <SelectContent>
                {departments?.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Class */}
          <div className="flex-1">
            <label className="block text-[14px] font-medium text-[#556374] mb-2">
              Class{' '}
              <span className="text-[#8C95A1] font-normal">(Optional)</span>
            </label>
            <Select
              value={selectedClassId ?? ''}
              onValueChange={setClass}
              disabled={!selectedDepartmentId || isClassesLoading}
            >
              <SelectTrigger className="w-full">
                {isClassesLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <SelectValue placeholder="Select an option" />
                )}
              </SelectTrigger>
              <SelectContent>
                {classes?.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subclass */}
          <div className="flex-1">
            <label className="block text-[14px] font-medium text-[#556374] mb-2">
              Subclass{' '}
              <span className="text-[#8C95A1] font-normal">(Optional)</span>
            </label>
            <Select
              value={selectedHierarchyId ?? ''}
              onValueChange={setSubclass}
              disabled={!selectedClassId || isSubclassesLoading}
            >
              <SelectTrigger className="w-full">
                {isSubclassesLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <SelectValue placeholder="Select an option" />
                )}
              </SelectTrigger>
              <SelectContent>
                {subclasses?.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Plan Name Section */}
      <div className="bg-white p-6 rounded-[12px] border border-[#D6DAE1] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[16px] font-semibold text-[#1C1C1C]">
            Plan Name
          </h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-[#3b82f6] hover:text-[#2563eb] cursor-pointer"
                >
                  <Info className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="bg-[#eef6ff] border border-[#bcdcff] text-[#1C1C1C] p-4 max-w-[280px]"
              >
                <p className="font-semibold text-[14px] mb-2">
                  Naming Convention
                </p>
                <p className="text-[13px] text-[#556374] mb-2">
                  Plan names should follow the format:
                </p>
                <ul className="text-[13px] text-[#556374] space-y-1 list-disc list-inside mb-2">
                  <li>Year (4 digits)</li>
                  <li>Department Code & Name</li>
                  <li>Class Code & Name</li>
                  <li>Subclass Code & Name (Optional)</li>
                </ul>
                <p className="text-[13px] text-[#556374]">
                  e.g.,{' '}
                  <span className="font-semibold">
                    2026-24-RIBBON-104-SEASONAL-1085-EASTERRIBBON
                  </span>
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex flex-col gap-3">
          {/* Input with validation states */}
          <div className="relative">
            <Input
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              placeholder="e.g., 2026-24-RIBBON-104-SEASONAL-1085-EASTERRIBBON"
              className={cn(
                'text-[14px] pr-10',
                // Valid state
                isValid && 'border-green-500 focus-visible:ring-green-500',
                // Invalid state
                errorMessage &&
                  !isValidating &&
                  'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {/* Status icon */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValidating && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
              {isValid && !isValidating && (
                <Check className="h-4 w-4 text-green-500" />
              )}
              {errorMessage && !isValidating && (
                <X className="h-4 w-4 text-red-500" />
              )}
            </div>
          </div>

          {/* Validation message */}
          {isValid && !isValidating && (
            <div className="flex items-center gap-1.5 text-[13px] text-green-600">
              <Check className="h-3.5 w-3.5" />
              <span>Name is valid and available</span>
            </div>
          )}
          {errorMessage && !isValidating && (
            <div className="flex items-center gap-1.5 text-[13px] text-red-500">
              <X className="h-3.5 w-3.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Suggestion */}
          {suggestion && (
            <div className="flex items-center gap-2 p-3 bg-[#F8F8F8] rounded-[8px]">
              <span className="text-[14px] text-[#556374]">Suggestions:</span>
              <button
                type="button"
                onClick={handleSuggestionClick}
                className={cn(
                  'text-[14px] font-medium text-[#CF1F2E] hover:underline cursor-pointer',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded'
                )}
              >
                {suggestion}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
